'use client';

import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import YachtCard from '@/components/ui/YachtCard';
import YachtCardSkeleton from '@/components/ui/YachtCardSkeleton';
import { YachtProps } from '@/types/generalInterFaces';
import { headerVariants } from '@/lib/animations';
import type { PageUrlMap } from '@/types/blog';
import { UrlBuilder } from '@/lib/url-builder';
import { shouldEmbedBlockPropsInDom } from '@/lib/embed-block-props-dom';
import { renderCmsSectionTitle } from '@/lib/render-cms-section-title';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';

interface CategoryData {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  cover_image?: string;
  type?: string;
}

interface Cats {
  id: string;
  title: string;
  yacht: YachtProps[];
}

function metaStr(m: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = m[k];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

function metaInt(m: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = m[k];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : parseInt(String(v), 10);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  return undefined;
}

function mapApiProductToYachtProps(
  product: Record<string, unknown>,
  urlBuilder: UrlBuilder | null,
): YachtProps {
  const gallery = (Array.isArray(product.gallery) ? product.gallery : []) as Array<{
    type?: string;
    url?: string;
    alt?: string;
  }>;
  const medias = gallery
    .filter((item) => item.type === 'image' && item.url)
    .map((item) => ({
      src: String(item.url),
      alt: item.alt || String(product.name || '') || 'Yacht',
    }));

  const metas = (product.metas as Record<string, unknown>) || {};
  const name = (product.name as string) || '';

  const cabinRaw =
    product.cabin != null && String(product.cabin).trim() !== ''
      ? String(product.cabin)
      : metaStr(metas, 'cabin_count', 'cabin');
  const lengthRaw =
    product.length != null && String(product.length).trim() !== ''
      ? String(product.length)
      : metaStr(metas, 'yacht_length', 'length');
  const guestCap =
    (typeof product.guest_capacity === 'number' ? product.guest_capacity : undefined) ??
    metaInt(metas, 'guest_capacity', 'passenger_capacity', 'yacht_guest_capacity', 'capacity', 'pax_count');
  const wcFromProduct = typeof product.wc === 'number' && product.wc > 0 ? product.wc : undefined;
  const wcFromMeta = metaInt(metas, 'bathroom_wc', 'wc');
  const wc = wcFromProduct ?? (wcFromMeta !== undefined && wcFromMeta > 0 ? wcFromMeta : 0);

  return {
    code:
      (product.code as string | undefined) ??
      (metas.code as string | undefined) ??
      (metas.yacht_id != null && String(metas.yacht_id).trim() !== ''
        ? String(metas.yacht_id).trim().replace(/^yacht-/i, '')
        : null) ??
      (product.slug as string | undefined) ??
      String(product.id ?? ''),
    title: name,
    medias:
      medias.length > 0
        ? medias
        : [{ src: '/placeholder-yacht.jpg', alt: name || 'Yacht' }],
    imageCount: medias.length,
    videoCount: gallery.filter((item) => item.type === 'video').length,
    start: String(product.start_location || metaStr(metas, 'start_location', 'yacht_marina') || ''),
    cabin: cabinRaw,
    guestCapacity: guestCap,
    length: lengthRaw,
    wc,
    yachtClass: (product.yacht_class as string | undefined) || metaStr(metas, 'yacht_class') || undefined,
    yachtType: (product.yacht_type as string | undefined) || metaStr(metas, 'yacht_type') || undefined,
    builtYear: product.built_year as string | undefined,
    beam: (product.beam as string | undefined) || metaStr(metas, 'yacht_beam') || undefined,
    flag: (product.flag as string | undefined) || metaStr(metas, 'yacht_flag') || undefined,
    brand: (product.brand as string | undefined) || metaStr(metas, 'yacht_brand') || undefined,
    rentType: String(product.rent_type || ''),
    minRentDay: String(product.min_rent_day || product.min_rental_duration || '1'),
    price: {
      price: String(product.price || product.base_price || '0'),
      type: String(product.rent_type || 'daily'),
    },
    slug: product.slug as string | undefined,
    url: urlBuilder
      ? urlBuilder.buildProductDetailUrl(
          String(product.type || 'yacht'),
          String(product.slug ?? ''),
          (product.category as { slug?: string } | undefined)?.slug,
        )
      : undefined,
    calculated_price: product.calculated_price as YachtProps['calculated_price'],
    productId: typeof product.id === 'number' ? product.id : Number(product.id) || undefined,
  };
}

interface YachtCatTabsProps {
  categoryIds?: number[];
  languageId?: number;
  language?: string;
  languageCode?: string;
  categories?: CategoryData[];
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  title?: string;
  subTitle?: string;
  __pageContext?: {
    languageId?: number;
    language?: string;
    languageCode?: string;
  };
}

const DEFAULT_TITLE = 'Experience Excellence on Every Voyage!';

/** Kategori listesi: `ProductCategoriesContext` doluysa ek fetch yok; aksi halde `/api/product-categories`. @see `frontend/docs/tabs-and-header-data.md` */
export default function YachtCatTabs({
  categoryIds: propCategoryIds,
  languageId: propLanguageId,
  language: propLanguage,
  languageCode: propLanguageCode,
  categories: initialCategories,
  pageUrls,
  defaultLanguage = 'en',
  title: titleProp,
  subTitle: subTitleProp,
  __pageContext,
}: YachtCatTabsProps = {}) {
  const languageId = propLanguageId ?? __pageContext?.languageId;
  const resolvedLanguage =
    propLanguage || propLanguageCode || __pageContext?.language || __pageContext?.languageCode;

  const urlBuilder = useMemo(
    () =>
      pageUrls ? new UrlBuilder(pageUrls, resolvedLanguage || 'en', defaultLanguage) : null,
    [pageUrls, resolvedLanguage, defaultLanguage],
  );

  const [categories, setCategories] = useState<CategoryData[]>(initialCategories || []);
  const [loading, setLoading] = useState(!initialCategories);
  const [error, setError] = useState<string | null>(null);
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredTabIndex, setHoveredTabIndex] = useState<number | null>(null);
  const [componentProps, setComponentProps] = useState<{
    categoryIds?: number[];
    title?: string;
    subTitle?: string;
  }>({});
  const [yachtsByCategory, setYachtsByCategory] = useState<Record<number, YachtProps[]>>({});
  const [yachtsLoading, setYachtsLoading] = useState<Record<number, boolean>>({});
  const loadedCategoryIdsRef = useRef<Set<number>>(new Set());
  const inflightCategoryIdsRef = useRef<Set<number>>(new Set());

  const hasInitialData = Boolean(initialCategories && initialCategories.length > 0);

  const { variables: contextVariables } = useLanguageVariables();
  const [variables, setVariables] = useState<Record<string, string>>({});
  useEffect(() => {
    if (contextVariables && Object.keys(contextVariables).length > 0) {
      setVariables(contextVariables);
    }
  }, [contextVariables]);

  const getVariable = (key: string, fallback: string): string => variables[key] || fallback;

  useEffect(() => {
    if (!shouldEmbedBlockPropsInDom()) return;
    const updateProps = () => {
      const element = document.querySelector('[data-component-path="sections/YachtCatTabs"]');
      if (element) {
        const propsAttr = element.getAttribute('data-component-props');
        if (propsAttr) {
          try {
            const parsed = JSON.parse(propsAttr) as {
              categoryIds?: number[];
              title?: string;
              subTitle?: string;
            };
            setComponentProps(parsed);
          } catch {
            /* ignore */
          }
        }
      }
    };

    updateProps();
    const observer = new MutationObserver(updateProps);
    const el = document.querySelector('[data-component-path="sections/YachtCatTabs"]');
    if (el) {
      observer.observe(el, { attributes: true, attributeFilter: ['data-component-props'] });
    }
    return () => observer.disconnect();
  }, []);

  const categoryIds = propCategoryIds ?? componentProps.categoryIds;

  const sectionTitle =
    (titleProp && String(titleProp).trim()) ||
    (componentProps.title && String(componentProps.title).trim()) ||
    DEFAULT_TITLE;
  const sectionSubTitle =
    (subTitleProp && String(subTitleProp).trim()) ||
    (componentProps.subTitle && String(componentProps.subTitle).trim()) ||
    undefined;

  useEffect(() => {
    if (hasInitialData) {
      setLoading(false);
      return;
    }

    async function fetchCategories() {
      if (!categoryIds || categoryIds.length === 0) {
        setLoading(false);
        setCategories([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (languageId) {
          params.set('language_id', String(languageId));
        }
        categoryIds.forEach((id) => params.append('category_ids[]', String(id)));
        if (resolvedLanguage) {
          params.set('language', resolvedLanguage);
        }

        const response = await fetch(`/api/product-categories?${params.toString()}`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            (errorData as { message?: string }).message ||
            (errorData as { error?: string }).error ||
            `API error: ${response.status}`;
          throw new Error(errorMessage);
        }

        const data = await response.json();
        setCategories(data.data || []);
      } catch (err) {
        console.error('YachtCatTabs: kategoriler yüklenirken hata:', err);
        setError(err instanceof Error ? err.message : 'Kategoriler yüklenemedi');
        setCategories([]);
      } finally {
        setLoading(false);
      }
    }

    void fetchCategories();
  }, [languageId, categoryIds, hasInitialData, resolvedLanguage]);

  const categoryListIdentity = useMemo(() => categories.map((c) => c.id).join(','), [categories]);

  useEffect(() => {
    loadedCategoryIdsRef.current.clear();
    inflightCategoryIdsRef.current.clear();
    setYachtsByCategory({});
    setYachtsLoading({});
  }, [categoryListIdentity]);

  const fetchYachtsForCategory = useCallback(
    async (categoryId: number, signal?: AbortSignal) => {
      if (loadedCategoryIdsRef.current.has(categoryId)) return;
      if (inflightCategoryIdsRef.current.has(categoryId)) return;
      inflightCategoryIdsRef.current.add(categoryId);
      setYachtsLoading((prev) => ({ ...prev, [categoryId]: true }));
      try {
        const params = new URLSearchParams();
        if (languageId) {
          params.set('language_id', String(languageId));
        }
        if (resolvedLanguage) {
          params.set('language', resolvedLanguage);
        }
        params.set('category_id', String(categoryId));
        params.set('is_active', '1');
        params.set('limit', '10');
        params.set('calculate_prices', 'true');

        const response = await fetch(`/api/products?${params.toString()}`, { signal });

        if (!response.ok) {
          setYachtsByCategory((prev) => ({ ...prev, [categoryId]: [] }));
          loadedCategoryIdsRef.current.add(categoryId);
          return;
        }

        const data = await response.json();
        const products = (data.data || []) as Record<string, unknown>[];
        const transformed = products.map((product) => mapApiProductToYachtProps(product, urlBuilder));
        setYachtsByCategory((prev) => ({ ...prev, [categoryId]: transformed }));
        loadedCategoryIdsRef.current.add(categoryId);
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        console.error(`YachtCatTabs: kategori ${categoryId} için yatlar yüklenirken hata:`, e);
        setYachtsByCategory((prev) => ({ ...prev, [categoryId]: [] }));
        loadedCategoryIdsRef.current.add(categoryId);
      } finally {
        inflightCategoryIdsRef.current.delete(categoryId);
        setYachtsLoading((prev) => ({ ...prev, [categoryId]: false }));
      }
    },
    [languageId, resolvedLanguage, urlBuilder],
  );

  useEffect(() => {
    if (categories.length === 0) return;
    const cat = categories[activeIndex];
    if (!cat) return;
    const ac = new AbortController();
    void fetchYachtsForCategory(cat.id, ac.signal);
    return () => ac.abort();
  }, [categories, activeIndex, fetchYachtsForCategory]);

  useEffect(() => {
    if (categories.length === 0 || hoveredTabIndex == null) return;
    if (hoveredTabIndex === activeIndex) return;
    const cat = categories[hoveredTabIndex];
    if (!cat) return;
    void fetchYachtsForCategory(cat.id);
  }, [hoveredTabIndex, activeIndex, categories, fetchYachtsForCategory]);

  const catsWithYacht: Cats[] = categories.map((category) => ({
    id: String(category.id),
    title: category.name,
    yacht: yachtsByCategory[category.id] || [],
  }));

  if (!loading && categories.length === 0) {
    return (
      <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32 lg:py-40">
        <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-6 sm:p-8 text-sm sm:text-base">
          {!categoryIds || categoryIds.length === 0
            ? getVariable(
                'categories_no_selection',
                "Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component'i seçin ve \"Kategorileri Seç\" butonunu kullanın.",
              )
            : getVariable('categories_not_found', 'Seçilen kategoriler bulunamadı.')}
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0">
        {/* Başlık: bloktan gelen metin; yükleme mesajı bu blokta değil, aşağıdaki içerik alanında */}
        <motion.div
          className="w-full max-w-2xl lg:w-5/12 lg:max-w-none px-0 sm:px-2 lg:px-0 mx-auto flex flex-col gap-4 sm:gap-6 mb-6 justify-center items-center min-w-0"
          variants={headerVariants}
        >
          {sectionSubTitle ? (
            <span
              className="font-display font-medium text-alsero-first text-xl sm:text-2xl md:text-3xl lg:text-4xl"
              data-prop-path="subTitle"
            >
              {renderCmsSectionTitle(sectionSubTitle)}
            </span>
          ) : (
            <span
              className="block h-9 lg:h-10 w-56 max-w-[85%] rounded-lg bg-gray-200/70 animate-pulse"
              aria-label={getVariable('yacht_cat_tabs.subtitle_placeholder', 'Subtitle')}
            />
          )}
          <h2
            className="font-bold text-alsero-text-dark text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight"
            data-prop-path="title"
          >
            {renderCmsSectionTitle(sectionTitle)}
          </h2>
        </motion.div>

        <div
          className="flex flex-col gap-4 sm:gap-6 pb-12 sm:pb-16 min-w-0"
          aria-busy="true"
          aria-live="polite"
        >
          <p className="text-center text-xs sm:text-sm text-alsero-light-gray px-2">
            {getVariable('yacht_cat_tabs.loading_categories', 'Preparing categories…')}
          </p>
          <div className="flex flex-row flex-wrap gap-2 sm:gap-3 justify-center max-w-full min-w-0">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-12 sm:h-14 min-w-[5.5rem] sm:min-w-[7rem] max-w-[10rem] flex-1 basis-[calc(50%-0.375rem)] sm:basis-auto rounded-lg bg-gray-200/80 animate-pulse"
              />
            ))}
          </div>
          <Swiper
            modules={[Autoplay]}
            loop={false}
            autoplay={false}
            slidesPerView={1}
            spaceBetween={16}
            breakpoints={{
              640: { slidesPerView: 1, spaceBetween: 20 },
              768: { slidesPerView: 2, spaceBetween: 20 },
              1024: { slidesPerView: 3, spaceBetween: 20 },
              1280: { slidesPerView: 3, spaceBetween: 24 },
              1536: { slidesPerView: 4, spaceBetween: 24 },
            }}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <SwiperSlide key={index} className="py-6 sm:py-10">
                <YachtCardSkeleton index={index} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32">
        <div className="text-center text-red-500 text-sm sm:text-base">
          {getVariable('yacht_cat_tabs.error_prefix', 'Error:')} {error}
        </div>
      </section>
    );
  }

  const finalCats = catsWithYacht.length > 0 ? catsWithYacht : [];

  if (finalCats.length === 0) {
    return (
      <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 py-16 sm:py-24 md:py-32 lg:py-40">
        <div className="text-center text-alsero-light-gray border-2 border-dashed border-gray-200 rounded-2xl p-6 sm:p-8 text-sm sm:text-base">
          {getVariable(
            'categories_no_selection',
            "Bu component için kategori seçimi yapılmamış. Lütfen sayfa editöründe component'i seçin ve \"Kategorileri Seç\" butonunu kullanın.",
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="w-full lg:w-8/12 xl:w-9/12 mx-auto px-4 sm:px-6 lg:px-0 min-w-0">
      <motion.div
        className="w-full max-w-2xl lg:w-5/12 lg:max-w-none px-0 sm:px-2 lg:px-0 mx-auto flex flex-col gap-4 sm:gap-6 mb-8 sm:mb-10 justify-center items-center min-w-0"
        variants={headerVariants}
      >
        {/* Düz DOM + data-prop-path: trait panel / canvas metni block_json ile senkron (motion içinde değil) */}
        {sectionSubTitle ? (
          <span
            className="font-display font-medium text-alsero-first text-xl sm:text-2xl md:text-3xl lg:text-4xl"
            data-prop-path="subTitle"
          >
            {renderCmsSectionTitle(sectionSubTitle)}
          </span>
        ) : null}
        <h2
          className="font-bold text-alsero-text-dark text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight"
          data-prop-path="title"
        >
          {renderCmsSectionTitle(sectionTitle)}
        </h2>
      </motion.div>

      <div className="flex flex-col gap-4 sm:gap-6 min-w-0">
        <TabGroup selectedIndex={activeIndex} onChange={setActiveIndex}>
          <TabList className="relative flex max-w-full min-w-0 flex-row flex-nowrap justify-start gap-2 overflow-x-auto overscroll-x-contain pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden touch-pan-x sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0 px-0.5 sm:px-0">
            {(() => {
              const indicatorIdx = hoveredTabIndex ?? activeIndex;
              return finalCats.map((cat, index) => {
                return (
                  <Tab key={cat.id} as={Fragment}>
                    <button
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      onMouseEnter={() => setHoveredTabIndex(index)}
                      onMouseLeave={() => setHoveredTabIndex(null)}
                      className="relative z-10 shrink-0 cursor-pointer whitespace-nowrap transition-colors duration-300 ease-in-out py-2.5 px-3 sm:py-3 sm:px-5 md:py-4 md:px-8 bg-white shadow-yacht-tab text-sm sm:text-base md:text-lg text-alsero-second text-center hover:bg-alsero-first rounded-lg hover:text-white data-[selected]:bg-alsero-first data-[selected]:text-white leading-tight"
                    >
                      <span className="relative">{cat.title}</span>

                      {indicatorIdx === index && (
                        <motion.div
                          layoutId="moving-underline"
                          className="absolute left-1/2 transform -translate-x-1/2 -bottom-[10px] w-0 h-0 border-l-[7px] border-r-[7px] border-t-[10px] border-l-transparent border-r-transparent border-t-alsero-first"
                          transition={{
                            type: 'spring',
                            stiffness: 500,
                            damping: 40,
                            mass: 0.6,
                          }}
                        />
                      )}
                    </button>
                  </Tab>
                );
              });
            })()}
          </TabList>
          <TabPanels>
            {finalCats.map((cat) => {
              const categoryId = Number(cat.id);
              const isLoadingYachts = yachtsLoading[categoryId] === true;
              const hasYachts = cat.yacht.length > 0;

              return (
                <TabPanel key={cat.id}>
                  {isLoadingYachts ? (
                    <div className="space-y-4">
                      <p className="sr-only">
                        {getVariable('yacht_cat_tabs.loading_yachts', 'Loading yachts…')}
                      </p>
                      <Swiper
                        modules={[Autoplay]}
                        loop={false}
                        autoplay={false}
                        slidesPerView={1}
                        spaceBetween={16}
                        breakpoints={{
                          640: { slidesPerView: 1, spaceBetween: 20 },
                          768: { slidesPerView: 2, spaceBetween: 20 },
                          1024: { slidesPerView: 3, spaceBetween: 20 },
                          1280: { slidesPerView: 3, spaceBetween: 24 },
                          1536: { slidesPerView: 4, spaceBetween: 24 },
                        }}
                      >
                        {Array.from({ length: 4 }).map((_, index) => (
                          <SwiperSlide key={index} className="py-6 sm:py-10">
                            <YachtCardSkeleton index={index} />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  ) : hasYachts ? (
                    <>
                      <Swiper
                        modules={[Autoplay]}
                        loop
                        autoplay={false}
                        slidesPerView={1}
                        spaceBetween={16}
                        onSwiper={(swiper) => setSwiperInstance(swiper)}
                        breakpoints={{
                          640: { slidesPerView: 1, spaceBetween: 20 },
                          768: { slidesPerView: 2, spaceBetween: 20 },
                          1024: { slidesPerView: 3, spaceBetween: 20 },
                          1280: { slidesPerView: 3, spaceBetween: 24 },
                          1536: { slidesPerView: 4, spaceBetween: 24 },
                        }}
                      >
                        {cat.yacht.map((yacht, index) => (
                          <SwiperSlide key={index} className="py-6 sm:py-10">
                            <YachtCard
                              yacht={yacht}
                              viewType="box"
                              index={index}
                              pageUrls={pageUrls}
                              defaultLanguage={defaultLanguage}
                              language={resolvedLanguage}
                              languageCode={resolvedLanguage}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <motion.div
                        initial={{ opacity: 1, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.6 }}
                        className="relative mt-4 flex items-center justify-center gap-3 sm:mt-8 sm:gap-5 lg:mt-14"
                      >
                        <motion.button
                          type="button"
                          onClick={() => swiperInstance?.slidePrev()}
                          className="p-3 sm:p-5 border border-alsero-dark-gray rounded-xl sm:rounded-2xl transition-all ease-in-out cursor-pointer duration-300 touch-manipulation"
                          aria-label="Previous slide"
                          whileHover={{
                            x: -5,
                            transition: { duration: 0.2 },
                          }}
                        >
                          <motion.svg
                            width="28"
                            height="25"
                            viewBox="0 0 28 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="fill-alsero-dark-gray"
                            whileHover={{ x: -3 }}
                            transition={{ duration: 0.2 }}
                          >
                            <path d="M0.447544 13.6676L10.5593 24.5202C10.848 24.83 11.2327 25 11.6429 25C12.0536 25 12.438 24.8297 12.7267 24.5202L13.6448 23.5346C13.9332 23.2253 14.0921 22.8122 14.0921 22.3717C14.0921 21.9314 13.9332 21.5044 13.6448 21.1951L7.7457 14.85L26.4873 14.85C27.3323 14.85 28 14.1401 28 13.2329V11.8396C28 10.9325 27.3323 10.1509 26.4873 10.1509L7.67877 10.1509L13.6445 3.77047C13.933 3.46068 14.0919 3.05879 14.0919 2.61829C14.0919 2.17829 13.933 1.77053 13.6445 1.46099L12.7265 0.478606C12.4378 0.168819 12.0533 -8.34465e-07 11.6427 -8.34465e-07C11.2325 -8.34465e-07 10.8478 0.171018 10.5591 0.480805L0.447317 11.3332C0.157987 11.6439 -0.00113344 12.0588 3.33786e-06 12.4998C-0.000906467 12.9422 0.157985 13.3573 0.447544 13.6676Z" />
                          </motion.svg>
                        </motion.button>
                        <motion.button
                          type="button"
                          onClick={() => swiperInstance?.slideNext()}
                          className="p-3 sm:p-5 border border-alsero-dark-gray rounded-xl sm:rounded-2xl transition-all ease-in-out cursor-pointer duration-300 touch-manipulation"
                          aria-label="Next slide"
                          whileHover={{
                            x: 5,
                            transition: { duration: 0.2 },
                          }}
                        >
                          <motion.svg
                            width="28"
                            height="25"
                            viewBox="0 0 28 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="fill-alsero-dark-gray"
                            whileHover={{ x: 3 }}
                            transition={{ duration: 0.2 }}
                          >
                            <path d="M27.5525 11.3324L17.4407 0.479833C17.152 0.170044 16.7673 2.93169e-06 16.3571 2.85997e-06C15.9464 2.78816e-06 15.562 0.170288 15.2733 0.479832L14.3552 1.46539C14.0668 1.77469 13.9079 2.18782 13.9079 2.62832C13.9079 3.06857 14.0668 3.49562 14.3552 3.80492L20.2543 10.15L1.51267 10.15C0.667668 10.15 -1.89881e-06 10.8599 -2.05742e-06 11.7671L-2.30103e-06 13.1604C-2.45964e-06 14.0675 0.667667 14.8491 1.51267 14.8491L20.3212 14.8491L14.3555 21.2295C14.067 21.5393 13.9081 21.9412 13.9081 22.3817C13.9081 22.8217 14.067 23.2295 14.3555 23.539L15.2735 24.5214C15.5622 24.8312 15.9467 25 16.3573 25C16.7675 25 17.1522 24.829 17.4409 24.5192L27.5527 13.6668C27.842 13.3561 28.0011 12.9412 28 12.5003C28.0009 12.0578 27.842 11.6427 27.5525 11.3324Z" />
                          </motion.svg>
                        </motion.button>
                      </motion.div>
                    </>
                  ) : (
                    <div className="py-12 sm:py-20 text-center text-alsero-light-gray text-sm sm:text-base px-2">
                      {getVariable(
                        'yacht_cat_tabs.no_yachts_in_category',
                        'No yachts found for this category yet.',
                      )}
                    </div>
                  )}
                </TabPanel>
              );
            })}
          </TabPanels>
        </TabGroup>
      </div>
    </section>
  );
}
